import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of, forkJoin, catchError, map } from 'rxjs';
import { StoreService } from '../services/store.service';
import { SecurityService } from '../services/security.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(
    private storeService: StoreService,
    private securityService: SecurityService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const profile = this.storeService.getProfile;
    const menu = this.storeService.optionsListMenu;

    // Si ya tenemos todo en el store, validar directamente
    if (profile && menu.length > 0) {
      if (profile.requires_billing_attention && !state.url.startsWith('/planes')) {
        this.router.navigate(['/planes']);
        return of(false);
      }
      return of(this.checkPermissions(state.url));
    }

    // Store vacío (recarga de página): rehidratar en paralelo
    const token = localStorage.getItem('JWT');
    if (!token) {
      this.router.navigate(['/login']);
      return of(false);
    }

    return forkJoin({
      profile: this.securityService.getProfile(),
      menu: this.securityService.getMenu(token)
    }).pipe(
      map(({ profile, menu }) => {
        // Guardar perfil
        this.storeService.storeProfile.next(profile.result);

        // Construir menú completo
        this.storeService.optionsListMenu = menu.result;
        this.storeService.optionsListMenu.push(
          { id: 998, name: 'Configuraciones', url: '/configuraciones', icon: 'assets/svg/Icon_configuraciones.svg' },
          { id: 999, name: 'Cerrar sesión', url: '/logout', icon: 'assets/svg/Icon_cerrar_sesion.svg' }
        );

        // Si hay problema de facturación, dejar solo Planes (id=2) y Cerrar sesión
        if (profile.result.requires_billing_attention) {
          this.storeService.optionsListMenu = this.storeService.optionsListMenu.filter(m => m.id === 2 || m.id === 999);
        }

        // Redirigir si hay problema de facturación y no está ya en /planes
        if (profile.result.requires_billing_attention && !state.url.startsWith('/planes')) {
          this.router.navigate(['/planes']);
          return false;
        }

        return this.checkPermissions(state.url);
      }),
      catchError(() => {
        this.router.navigate(['/login']);
        return of(false);
      })
    );
  }

  private checkPermissions(rutaSolicitada: string): boolean {
    const menu = this.storeService.optionsListMenu;
    const rutasPermitidas = menu.map(item => item.url);
    const hasPermits = rutasPermitidas.some(ruta => rutaSolicitada.startsWith(ruta));

    if (hasPermits) {
      return true;
    }

    const dashboard = menu.find(m => m.id === 1);
    this.router.navigate([dashboard?.url || '/']);
    return false;
  }
}
